import { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { getKeyboardRange, generateKeyboardKeys } from '../../utils/midiPiano';
import { getSetting, settingKeys } from '../../utils/settings';

import './MidiPiano.scss';

/*
 * A keyboard that lights up the notes a MIDI clip is sounding. Purely presentational
 * — it holds no timing of its own and simply renders whatever notes are passed.
 */
const MidiPiano = ({ midiJson, activeNotes, fullRange = false, showOctaveLabels = true }) => {
  const preferSharps = getSetting(settingKeys.preferSharps) !== false;

  const { low, high } = useMemo(() => getKeyboardRange(midiJson, fullRange), [midiJson, fullRange]);
  const { keys, whiteCount } = useMemo(
    () => generateKeyboardKeys(low, high, preferSharps),
    [low, high, preferSharps],
  );

  const isActive = (midi) => Boolean(activeNotes?.has(midi));

  return (
    <div className="MidiPiano">
      <div className="keyboard" style={{ '--white-count': whiteCount }}>
        <div className="whites">
          {keys
            .filter((key) => !key.isBlack)
            .map((key) => (
              <div
                key={key.midi}
                className={cx('key', 'key--white', { 'key--active': isActive(key.midi) })}
                data-note={key.note}
              >
                {showOctaveLabels && key.isOctaveStart && (
                  <span className="octave-label">
                    <span className="note">{key.note}</span>
                    {key.octave}
                  </span>
                )}
              </div>
            ))}
        </div>

        {keys
          .filter((key) => key.isBlack)
          .map((key) => (
            <div
              key={key.midi}
              className={cx('key', 'key--black', { 'key--active': isActive(key.midi) })}
              data-note={key.note}
              // Straddles the gap to the left of the white key it follows
              style={{ left: `calc(${key.whiteIndex} * var(--white-width) - var(--black-width) / 2)` }}
            />
          ))}
      </div>
    </div>
  );
};

MidiPiano.propTypes = {
  midiJson: PropTypes.object,
  /* MIDI numbers currently sounding, as a Set for O(1) lookup per key */
  activeNotes: PropTypes.instanceOf(Set),
  /* Draw all 88 keys rather than fitting the keyboard to the clip's range */
  fullRange: PropTypes.bool,
  showOctaveLabels: PropTypes.bool,
};

export default MidiPiano;
